import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { URL as API_BASE } from '../env';

// Set the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * PdfViewer - Renders a PDF using PDF.js with clickable links that open in new tabs.
 * Fetches PDF through backend proxy to bypass S3 CORS restrictions.
 *
 * Props:
 *  - src: S3 URL of the PDF to render
 *  - className: CSS class for the outer container
 *  - style: inline style for the outer container
 *  - title: accessible title
 */
export default function PdfViewer({ src, className = '', style = {}, title = 'PDF Viewer' }) {
    const containerRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scale, setScale] = useState(1.5);
    const renderTasksRef = useRef([]);
    const pagesRenderedRef = useRef(false);

    // Load the PDF document via backend proxy
    useEffect(() => {
        if (!src) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setPdfDoc(null);
        pagesRenderedRef.current = false;

        const proxyUrl = `${API_BASE}/api/company/pdf-proxy?url=${encodeURIComponent(src)}`;

        // Fetch PDF as ArrayBuffer through our proxy, then pass to PDF.js
        fetch(proxyUrl)
            .then(response => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.arrayBuffer();
            })
            .then(data => {
                if (cancelled) return;
                const loadingTask = pdfjsLib.getDocument({
                    data: new Uint8Array(data),
                    cMapUrl: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
                    cMapPacked: true,
                });
                return loadingTask.promise;
            })
            .then(doc => {
                if (!cancelled) {
                    setPdfDoc(doc);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    console.error('PDF load error:', err);
                    setError('Failed to load PDF');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [src]);

    // Render all pages
    const renderPages = useCallback(async () => {
        if (!pdfDoc || !containerRef.current || pagesRenderedRef.current) return;
        pagesRenderedRef.current = true;

        const container = containerRef.current;

        // Clear previous renders
        renderTasksRef.current.forEach(task => task.cancel?.());
        renderTasksRef.current = [];
        container.innerHTML = '';

        const numPages = pdfDoc.numPages;

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            try {
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale });

                // Page wrapper
                const pageDiv = document.createElement('div');
                pageDiv.style.position = 'relative';
                pageDiv.style.width = `${viewport.width}px`;
                pageDiv.style.height = `${viewport.height}px`;
                pageDiv.style.margin = '0 auto 8px auto';

                // Canvas for rendering
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.display = 'block';
                pageDiv.appendChild(canvas);

                container.appendChild(pageDiv);

                // Render the page
                const ctx = canvas.getContext('2d');
                const renderTask = page.render({ canvasContext: ctx, viewport });
                renderTasksRef.current.push(renderTask);

                await renderTask.promise;

                // Get link annotations and overlay clickable elements
                const annotations = await page.getAnnotations();
                const linkAnnotations = annotations.filter(a => a.subtype === 'Link' && a.url);

                if (linkAnnotations.length > 0) {
                    const annotationLayer = document.createElement('div');
                    annotationLayer.style.position = 'absolute';
                    annotationLayer.style.top = '0';
                    annotationLayer.style.left = '0';
                    annotationLayer.style.width = `${viewport.width}px`;
                    annotationLayer.style.height = `${viewport.height}px`;

                    linkAnnotations.forEach(annotation => {
                        const rect = annotation.rect;
                        // PDF coordinates: origin at bottom-left; transform to top-left
                        const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);

                        const left = Math.min(x1, x2);
                        const top = Math.min(y1, y2);
                        const width = Math.abs(x2 - x1);
                        const height = Math.abs(y2 - y1);

                        const link = document.createElement('a');
                        link.href = annotation.url;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.style.position = 'absolute';
                        link.style.left = `${left}px`;
                        link.style.top = `${top}px`;
                        link.style.width = `${width}px`;
                        link.style.height = `${height}px`;
                        link.style.cursor = 'pointer';
                        link.style.display = 'block';
                        link.style.backgroundColor = 'transparent';
                        link.style.border = 'none';
                        link.style.zIndex = '1';
                        link.title = annotation.url;

                        // Subtle hover effect
                        link.addEventListener('mouseenter', () => {
                            link.style.backgroundColor = 'rgba(0, 100, 255, 0.1)';
                            link.style.borderBottom = '2px solid rgba(0, 100, 255, 0.4)';
                        });
                        link.addEventListener('mouseleave', () => {
                            link.style.backgroundColor = 'transparent';
                            link.style.borderBottom = 'none';
                        });

                        annotationLayer.appendChild(link);
                    });

                    pageDiv.appendChild(annotationLayer);
                }
            } catch (err) {
                if (err.name !== 'RenderingCancelledException') {
                    console.error(`Error rendering page ${pageNum}:`, err);
                }
            }
        }
    }, [pdfDoc, scale]);

    // Render pages when doc loads or scale changes
    useEffect(() => {
        if (pdfDoc) {
            pagesRenderedRef.current = false;
            renderPages();
        }
    }, [pdfDoc, scale, renderPages]);

    if (!src) return null;

    return (
        <div className={className} style={{ overflow: 'auto', backgroundColor: '#525659', ...style }} title={title}>
            {/* Toolbar */}
            <div style={{
                position: 'sticky', top: 0, zIndex: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                padding: '8px 16px', backgroundColor: '#323639', borderBottom: '1px solid #474a4d',
            }}>
                <button
                    onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
                    style={toolbarBtnStyle}
                    title="Zoom Out"
                >−</button>
                <span style={{ color: '#ccc', fontSize: '13px', minWidth: '50px', textAlign: 'center' }}>
                    {Math.round(scale * 100)}%
                </span>
                <button
                    onClick={() => setScale(s => Math.min(3, s + 0.25))}
                    style={toolbarBtnStyle}
                    title="Zoom In"
                >+</button>
                <button
                    onClick={() => setScale(1.5)}
                    style={{ ...toolbarBtnStyle, fontSize: '12px', padding: '4px 10px' }}
                    title="Reset Zoom"
                >Reset</button>
                <span style={{ color: '#888', fontSize: '12px' }}>
                    {pdfDoc ? `${pdfDoc.numPages} page${pdfDoc.numPages !== 1 ? 's' : ''}` : ''}
                </span>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#ccc' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', marginBottom: '8px' }}>Loading PDF...</div>
                        <div style={{ width: '40px', height: '40px', border: '3px solid #555', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#ff6b6b' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', marginBottom: '8px' }}>{error}</div>
                        <button onClick={() => window.open(src, '_blank')} style={{ ...toolbarBtnStyle, padding: '6px 16px' }}>
                            Open PDF directly
                        </button>
                    </div>
                </div>
            )}

            {/* PDF pages container */}
            <div ref={containerRef} style={{ padding: '16px 0' }} />
        </div>
    );
}

const toolbarBtnStyle = {
    background: '#474a4d',
    border: '1px solid #5a5d60',
    color: '#ddd',
    padding: '4px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '1',
};
